import { useState, useRef, useEffect, useMemo } from "react";

interface DatePickerProps {
  value: string; // yyyy-MM-dd
  onChange: (value: string) => void;
  minDate?: string; // yyyy-MM-dd
  maxDate?: string | null; // yyyy-MM-dd
  placeholder?: string;
  className?: string;
  hasError?: boolean;
}

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Week starts on Monday (Belgian convention)
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

interface ParsedDate {
  year: number;
  month: number; // 0-based
  day: number;
}

function parseDate(dateStr?: string | null): ParsedDate | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m - 1, day: d };
}

function toDateString(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

/**
 * Day-level date picker styled identically to <MonthPicker/> — same trigger
 * button (formatted value + label + calendar icon) and dropdown, but with a
 * month-navigation header and a day grid. All min/max comparisons are plain
 * yyyy-MM-dd string comparisons — no Date parsing, no timezone pitfalls.
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select date",
  className = "",
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseDate(value), [value]);
  const min = useMemo(() => parseDate(minDate), [minDate]);
  const max = useMemo(() => parseDate(maxDate), [maxDate]);

  const initialView = selected ?? min ?? {
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
    day: 1,
  };
  const [viewYear, setViewYear] = useState(initialView.year);
  const [viewMonth, setViewMonth] = useState(initialView.month);

  useEffect(() => {
    if (isOpen) {
      const target = selected ?? min;
      if (target) {
        setViewYear(target.year);
        setViewMonth(target.month);
      }
    }
  }, [isOpen, selected, min]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDayDisabled = (day: number): boolean => {
    const dateVal = toDateString(viewYear, viewMonth, day);
    if (minDate && dateVal < minDate) return true;
    if (maxDate && dateVal > maxDate) return true;
    return false;
  };

  const isDaySelected = (day: number): boolean =>
    selected !== null &&
    selected.year === viewYear &&
    selected.month === viewMonth &&
    selected.day === day;

  const monthIndex = (y: number, m: number) => y * 12 + m;

  const canGoPrev = (): boolean =>
    !min || monthIndex(viewYear, viewMonth) > monthIndex(min.year, min.month);

  const canGoNext = (): boolean =>
    !max || monthIndex(viewYear, viewMonth) < monthIndex(max.year, max.month);

  const goPrev = () => {
    if (!canGoPrev()) return;
    setViewMonth((m) => (m === 0 ? 11 : m - 1));
    if (viewMonth === 0) setViewYear((y) => y - 1);
  };

  const goNext = () => {
    if (!canGoNext()) return;
    setViewMonth((m) => (m === 11 ? 0 : m + 1));
    if (viewMonth === 11) setViewYear((y) => y + 1);
  };

  const handleSelect = (day: number) => {
    onChange(toDateString(viewYear, viewMonth, day));
    setIsOpen(false);
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  // getDay(): 0=Sunday — shift so Monday is column 0
  const firstWeekday = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;

  const displayValue = useMemo(() => {
    if (!selected) return "";
    const d = String(selected.day).padStart(2, "0");
    const m = String(selected.month + 1).padStart(2, "0");
    return `${d}.${m}.${selected.year}`;
  }, [selected]);

  const displayLabel = useMemo(() => {
    if (!selected) return "";
    return `${selected.day} ${MONTHS_FULL[selected.month]} ${selected.year}`;
  }, [selected]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full border p-3 text-left bg-white dark:bg-primary-800 flex justify-between items-center transition-colors ${
          hasError
            ? "border-red-400"
            : isOpen
              ? "border-primary-500 dark:border-primary-400"
              : "border-gray-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-500"
        }`}
      >
        <div className="flex flex-col">
          {selected ? (
            <>
              <span className="text-sm font-medium text-primary-600 dark:text-white">{displayValue}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{displayLabel}</span>
            </>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">{placeholder}</span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-72 bg-white dark:bg-primary-800 border border-gray-200 dark:border-primary-700 rounded-lg shadow-xl animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Month navigation */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-primary-700">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canGoPrev()}
              className={`p-1 rounded-md transition-colors ${
                canGoPrev()
                  ? "hover:bg-gray-100 dark:hover:bg-primary-700 text-primary-600 dark:text-white"
                  : "text-gray-300 dark:text-primary-600 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-primary-700 dark:text-white select-none">
              {MONTHS_FULL[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext()}
              className={`p-1 rounded-md transition-colors ${
                canGoNext()
                  ? "hover:bg-gray-100 dark:hover:bg-primary-700 text-primary-600 dark:text-white"
                  : "text-gray-300 dark:text-primary-600 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Weekday header + day grid */}
          <div className="p-3">
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((wd) => (
                <span
                  key={wd}
                  className="text-center text-xs font-medium text-gray-400 dark:text-gray-500 select-none"
                >
                  {wd}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1">
              {Array.from({ length: firstWeekday }, (_, i) => (
                <span key={`pad-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const disabled = isDayDisabled(day);
                const active = isDaySelected(day);
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelect(day)}
                    className={`h-8 w-8 mx-auto rounded-md text-sm font-medium transition-all ${
                      active
                        ? "bg-primary-600 text-white shadow-sm"
                        : disabled
                          ? "text-gray-300 dark:text-primary-600 cursor-not-allowed"
                          : "text-primary-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-700"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
